import type { DownloadPartResult } from "./downloads.types";

// What the page shows for a course while a run works on it, and once it ends.
export type DownloadRowState =
  | { kind: "queued" }
  // `note` says why a retry is being waited for; it is null once none is.
  | { kind: "running"; note?: string | null }
  | {
      kind: "done";
      od: DownloadPartResult;
      quiz: DownloadPartResult;
      quizVideo: DownloadPartResult;
      odSummary: DownloadPartResult;
      trackerRowAdded: boolean;
      warnings: string[];
    }
  | { kind: "error"; message: string }
  // The run stopped before this course started.
  | { kind: "stopped" };

type DoneState = Extract<DownloadRowState, { kind: "done" }>;

type Part = "od" | "quiz" | "quizVideo" | "odSummary";

// In the order the page names them.
const PARTS: Array<{ part: Part; name: string }> = [
  { part: "od", name: "OD" },
  { part: "quiz", name: "quiz" },
  { part: "quizVideo", name: "quiz de video" },
  { part: "odSummary", name: "OD resumida" },
];

type Tally =
  | "downloaded"
  | "summaryDownloaded"
  | "noQuiz"
  | "noOd"
  | "noQuizVideo"
  | "noOdSummary"
  | "empty"
  | "error"
  | "stopped";

const MISSING_TALLY: Record<Part, Tally> = {
  od: "noOd",
  quiz: "noQuiz",
  quizVideo: "noQuizVideo",
  odSummary: "noOdSummary",
};

export type RowColor = "default" | "processing" | "success" | "error";

export interface RowDisplay {
  label: string;
  color: RowColor;
}

// What a finished course wrote, and what the platform lacked of what was asked
// for. A part that was not asked for is neither.
function partsOf(state: DoneState) {
  return {
    written: PARTS.filter(({ part }) => state[part] === "done"),
    missing: PARTS.filter(({ part }) => state[part] === "missing"),
  };
}

const namesOf = (parts: typeof PARTS, conjunction: string) =>
  parts.map(({ name }) => name).join(` ${conjunction} `);

function describeDone(state: DoneState): RowDisplay {
  const { written, missing } = partsOf(state);
  if (missing.length === 0) return { label: "✓ Listo", color: "success" };
  if (written.length === 0) {
    return { label: "Sin contenido en plataforma", color: "default" };
  }
  return {
    label: `✓ Solo ${namesOf(written, "y")} · sin ${namesOf(missing, "ni")} en plataforma`,
    color: "success",
  };
}

export function describeRow(state: DownloadRowState): RowDisplay {
  switch (state.kind) {
    case "queued":
      return { label: "En cola", color: "default" };
    case "running":
      return { label: "Descargando…", color: "processing" };
    case "error":
      return { label: `✗ Error: ${state.message}`, color: "error" };
    case "stopped":
      return { label: "Sin descargar", color: "default" };
    case "done":
      return describeDone(state);
  }
}

export const isSettled = (state: DownloadRowState): boolean =>
  state.kind === "done" || state.kind === "error" || state.kind === "stopped";

// A course that wrote at least one part was downloaded; each part the
// platform lacked is a note on top. One that wrote none is not a download.
function talliesOf(state: DownloadRowState): Tally[] {
  switch (state.kind) {
    case "done": {
      const { written, missing } = partsOf(state);
      // An OD resumida is the whole of its run: it is never mixed with parts.
      if (state.odSummary === "done") return ["summaryDownloaded"];
      if (missing.length === 0) return ["downloaded"];
      if (written.length === 0) return ["empty"];
      return ["downloaded", ...missing.map(({ part }) => MISSING_TALLY[part])];
    }
    case "error":
      return ["error"];
    case "stopped":
      return ["stopped"];
    default:
      return [];
  }
}

const SUMMARY_PARTS: Array<[Tally, (count: number) => string]> = [
  ["downloaded", (n) => `${n} ${n === 1 ? "descargado" : "descargados"}`],
  [
    "summaryDownloaded",
    (n) =>
      `${n} ${n === 1 ? "OD resumida descargada" : "ODs resumidas descargadas"}`,
  ],
  ["noQuiz", (n) => `${n} sin quiz en plataforma`],
  ["noOd", (n) => `${n} sin OD en plataforma`],
  ["noQuizVideo", (n) => `${n} sin quiz de video en plataforma`],
  ["noOdSummary", (n) => `${n} sin OD resumida en plataforma`],
  ["empty", (n) => `${n} sin contenido en plataforma`],
  ["error", (n) => `${n} ${n === 1 ? "error" : "errores"}`],
  ["stopped", (n) => `${n} sin descargar`],
];

// «38 descargados · 2 sin quiz en plataforma · 1 error»: a course counts under
// one outcome, except one that lacks a part on the platform but wrote another:
// it was downloaded too, and each missing part is a note on top. An outcome
// nobody had is left out.
export function summaryLine(states: DownloadRowState[]): string {
  const counts: Partial<Record<Tally, number>> = {};
  states.forEach((state) => {
    talliesOf(state).forEach((key) => {
      counts[key] = (counts[key] ?? 0) + 1;
    });
  });
  return SUMMARY_PARTS.flatMap(([tally, say]) => {
    const count = counts[tally];
    return count ? [say(count)] : [];
  }).join(" · ");
}

export function trackerNotice(states: DownloadRowState[]): string | null {
  const added = states.filter(
    // Adding rows is the normal thing for an OD resumida: no notice.
    (state) =>
      state.kind === "done" &&
      state.trackerRowAdded &&
      state.odSummary === "skipped",
  ).length;
  if (added === 0) return null;
  return added === 1
    ? "1 curso sin fila: añadido al tracker"
    : `${added} cursos sin fila: añadidos al tracker`;
}
